import React, { useState, useEffect, FC } from 'react';
import { Box, Checkbox, FormControlLabel } from '@mui/material';
import TextField from '@mui/material/TextField';
import cx from 'classnames';
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import { VALIDATION_REGEX } from 'components/common/forms/constants';
import {
  fetchMessengerConfiguration,
  updateMessengerConfiguration,
} from 'api/admin/businesses/businesses';
import { useCalculateCustomerMetrics } from './mutations/useCalculateCustomerMetrics';

interface Props {
  businessId: number;
  onClose: () => void;
}

const DISABLE_SMS_BOT_DEFAULT = true;

const EditMessengerConfiguration: FC<Props> = ({ businessId, onClose }) => {
  const [messengerConfig, setMessengerConfig] = useState({
    twilioNumber: '',
    trxPhone: '',
    smsEscalationEmail: '',
    smsEscalationPhoneNumber: '',
    disableSmsBot: DISABLE_SMS_BOT_DEFAULT,
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { mutate: calculateCustomerMetrics, isLoading: isForceSyncLoading } =
    useCalculateCustomerMetrics();

  const onForceSync = () => {
    calculateCustomerMetrics({ businessId });
  };

  const retrieveMessengerConfiguration = async () => {
    try {
      setLoading(true);
      const { data } = await fetchMessengerConfiguration(businessId);

      setMessengerConfig({
        twilioNumber: data.twilioPhone || '',
        trxPhone: data.trxPhone || '',
        smsEscalationEmail: data.smsEscalationEmail || '',
        smsEscalationPhoneNumber: data.smsEscalationPhoneNumber || '',
        disableSmsBot: data.disableSmsBot ?? DISABLE_SMS_BOT_DEFAULT,
      });
    } catch (error: any) {
      setError(error?.response?.data?.error || 'Error retrieving configuration');
    } finally {
      setLoading(false);
    }
  };

  const onSave = async () => {
    const {
      twilioNumber,
      trxPhone,
      smsEscalationEmail,
      smsEscalationPhoneNumber,
      disableSmsBot,
    } = messengerConfig;

    if (!twilioNumber) {
      setError('Twilio number is required');
      return;
    }

    if (!twilioNumber.startsWith('+')) {
      setError('Twilio number must start with +');
      return;
    }

    if (twilioNumber.length !== 12) {
      setError('Twilio number must be 12 digits');
      return;
    }

    if (smsEscalationPhoneNumber && !smsEscalationPhoneNumber.startsWith('+')) {
      setError('SMS Escalation Phone Number must start with +');
      return;
    }

    if (smsEscalationPhoneNumber && smsEscalationPhoneNumber.length !== 12) {
      setError('SMS Escalation Phone Number must be 12 digits');
      return;
    }

    if (trxPhone && !trxPhone.startsWith('+')) {
      setError('SMS Transactional Phone Number must start with +');
      return;
    }

    if (trxPhone && trxPhone.length !== 12) {
      setError('SMS Transactional Phone Number must be 12 digits');
      return;
    }

    if (smsEscalationEmail && !VALIDATION_REGEX.email.test(smsEscalationEmail)) {
      setError('SMS Escalation Email must be a valid email address');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await updateMessengerConfiguration(
        {
          twilioPhone: twilioNumber,
          trxPhone,
          smsEscalationEmail,
          smsEscalationPhoneNumber,
          disableSmsBot,
        },
        businessId,
      );
      onClose();
    } catch (error: any) {
      setError(error?.response?.data?.error || 'Something went wrong while updating configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    retrieveMessengerConfiguration();
  }, [businessId]);

  return (
    <div className="edit-messenger-configuration-container">
      <h1 className="edit-messenger-configuration-header">Edit Messenger Configuration</h1>
      {loading && <CentsLoader />}
      {!loading && (
        <div className="edit-messenger-configuration-rows">
          <div className="edit-messenger-configuration-row">
            <TextField
              label={'Marketing Phone Number'}
              value={messengerConfig.twilioNumber}
              onChange={(e) =>
                setMessengerConfig((prev) => ({ ...prev, twilioNumber: e.target.value }))
              }
            />
          </div>

          <div className="edit-messenger-configuration-row">
            <TextField
              label={'Transactional Phone Number'}
              value={messengerConfig.trxPhone}
              onChange={(e) =>
                setMessengerConfig((prev) => ({ ...prev, trxPhone: e.target.value }))
              }
            />
          </div>

          <div className="edit-messenger-configuration-row">
            <TextField
              label="Escalation Phone Number"
              value={messengerConfig.smsEscalationPhoneNumber}
              onChange={(e) =>
                setMessengerConfig((prev) => ({
                  ...prev,
                  smsEscalationPhoneNumber: e.target.value,
                }))
              }
            />
          </div>
          <div className="edit-messenger-configuration-row">
            <TextField
              label="SMS Escalation Email"
              value={messengerConfig.smsEscalationEmail}
              onChange={(e) =>
                setMessengerConfig((prev) => ({ ...prev, smsEscalationEmail: e.target.value }))
              }
            />
          </div>
          <div className="edit-messenger-configuration-row">
            <FormControlLabel
              label="Disable SMS Bot"
              control={
                <Checkbox
                  checked={messengerConfig.disableSmsBot}
                  onChange={(e) =>
                    setMessengerConfig((prev) => ({ ...prev, disableSmsBot: e.target.checked }))
                  }
                />
              }
              sx={{
                border: '1px solid',
                borderColor: 'rgba(0, 0, 0, 0.23)',
                borderRadius: '4px',
                width: 300,
                padding: '6px',
                marginLeft: '10px',
              }}
            />
          </div>
        </div>
      )}
      {error && <p className="error-text">{error}</p>}
      <div>
        <button
          className={cx('force-sync-submit-button', 'button', isForceSyncLoading && 'disabled')}
          disabled={isForceSyncLoading}
          onClick={onForceSync}
        >
          Force Sync
        </button>
        <button
          className={cx(
            'edit-messenger-configuration-submit-button',
            'button',
            loading && 'disabled',
          )}
          disabled={loading}
          onClick={onSave}
        >
          Save Changes
        </button>
      </div>
    </div>
  );
};

export default EditMessengerConfiguration;
